<template>
  <div>
    <v-dialog
      scrollable
      width="90vw"
      :persistent="hasChanges"
      :value="open"
      @click:outside="handleClose"
      @keydown.esc="handleClose"
    >
      <Gallery
        v-if="currentView === VIEWS.GALLERY"
        :allowMultipleSelect="maxImages > 1"
        :canAdd="!readOnly && images.length < maxImages"
        :canSave="!readOnly"
        :images="images"
        :isUploading="isUploading"
        :readOnly="readOnly"
        :selectedImageIndex="selectedImageIndex"
        @added="handleAddedImages"
        @close="handleClose"
        @delete="isDeleteConfirmationOpen = true"
        @edit="handleEditImage"
        @save="handleSave"
        @selected="handleSelectedImage"
      />
      <Editor
        v-if="currentView === VIEWS.EDITOR"
        :image="selectedImage"
        @save="handleEditedImage"
        @back="handleCancelImageEdition"
      />

      <v-overlay absolute :opacity="0.8" :value="isUploading">
        <v-container class="d-flex flex-column align-center justify-center">
          <v-progress-circular
            class="align-self-center"
            color="primary"
            size="52"
            width="8"
            :value="uploadProgressPercentage"
          />

          <v-spacer class="my-2" />

          <h3>
            {{
              $t('media.uploading_progress_message', {
                totalUploadedFiles,
                totalFiles: images.length,
              })
            }}
          </h3>
        </v-container>
      </v-overlay>
    </v-dialog>

    <DeleteConfirmationModal
      :show="isDeleteConfirmationOpen"
      :title="$t('media.delete_confirmation_title')"
      :text="$t('media.delete_confirmation_message')"
      @cancel="isDeleteConfirmationOpen = false"
      @delete="removeSelectedImage"
    />

    <DiscardChangesConfirmation
      :open="isDiscardConfirmationOpen"
      :saveButtonText="$t('media.discard_changes_save_and_close_button')"
      @cancel="isDiscardConfirmationOpen = false"
      @discard="handleDiscardChanges"
      @save="handleSave"
    />
  </div>
</template>

<script>
import _ from 'lodash'

import ReverMediaSDK from '@/lib/ReverMediaSDK'
import mixin from '@/mixins/mixin.vue'

import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

import Gallery from './Gallery.vue'
import Editor from './Editor.vue'
import DiscardChangesConfirmation from './DiscardChangesConfirmation.vue'

const VIEWS = {
  GALLERY: 'gallery',
  EDITOR: 'editor',
}

export default {
  name: 'MediaManagerModal',

  mixins: [mixin],

  components: {
    Gallery,
    Editor,
    DeleteConfirmationModal,
    DiscardChangesConfirmation,
  },

  props: {
    open: Boolean,

    maxImages: Number,

    initialImages: {
      type: Array,
      default: () => [],
    },

    initialSelectedImageIndex: Number,

    readOnly: Boolean,
  },

  data() {
    return {
      currentView: VIEWS.GALLERY,
      images: [...this.initialImages],
      isDeleteConfirmationOpen: false,
      isDiscardConfirmationOpen: false,
      isUploading: false,
      selectedImageIndex:
        this.initialSelectedImageIndex ?? this.initialImages.length - 1,
      totalUploadedFiles: 0,
      VIEWS,
    }
  },

  computed: {
    hasChanges() {
      return !_.isEqual(this.initialImages, this.images)
    },

    selectedImage() {
      return this.images?.[this.selectedImageIndex] ?? null
    },

    uploadProgressPercentage() {
      if (this.images.length === 0) return 0
      return Math.floor((this.totalUploadedFiles / this.images.length) * 100)
    },
  },

  watch: {
    initialImages(images) {
      this.images = [...images]
      this.selectedImageIndex = images.length - 1
    },

    initialSelectedImageIndex(index) {
      this.selectedImageIndex = index
    },
  },

  methods: {
    handleSelectedImage(imageIndex) {
      this.selectedImageIndex = imageIndex
    },

    handleAddedImages(addedImages) {
      const nextTotalImages = this.images.length + addedImages.length

      if (nextTotalImages > this.maxImages) {
        return this.showError(
          this.$t('media.limit_images_error', { maxImages: this.maxImages })
        )
      }

      this.images = [...this.images, ...addedImages]
      this.selectedImageIndex = this.images.length - 1
    },

    handleEditImage() {
      this.currentView = VIEWS.EDITOR
    },

    handleCancelImageEdition() {
      this.currentView = VIEWS.GALLERY
    },

    handleEditedImage(editedImage) {
      this.images.splice(this.selectedImageIndex, 1, editedImage)
      this.images = [...this.images]
      this.currentView = VIEWS.GALLERY
    },

    removeSelectedImage() {
      this.images.splice(this.selectedImageIndex, 1)
      this.images = [...this.images]
      this.selectedImageIndex = this.images.length - 1
      this.isDeleteConfirmationOpen = false
    },

    handleClose() {
      if (!this.hasChanges) {
        return this.$emit('close')
      }

      this.isDiscardConfirmationOpen = true
    },

    handleDiscardChanges() {
      this.isDiscardConfirmationOpen = false
      this.$emit('close')
    },

    async handleSave() {
      this.isDiscardConfirmationOpen = false

      try {
        this.totalUploadedFiles = 0

        const mediaObjects = []

        for (const image of this.images) {
          const mediaObject = await this.uploadImage(image)
          this.totalUploadedFiles++

          await this.$nextTick()
          mediaObjects.push(mediaObject)
        }

        this.$emit('change', mediaObjects)
      } catch {
        this.showError(this.$t('media.upload_error'))
      } finally {
        this.isUploading = false
        await this.$nextTick()
        this.totalUploadedFiles = 0
      }
    },

    async uploadImage(image) {
      const isMediaObject = image?.id
      if (isMediaObject) return image

      this.isUploading = true

      const blob = this.dataURIToBlob(image)

      const mediaObject = await ReverMediaSDK.uploadMedia(blob)
      return mediaObject
    },

    dataURIToBlob(dataURI) {
      const binary = window.atob(dataURI.split(',')[1])
      const array = []

      for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i))
      }

      return new window.Blob([new Uint8Array(array)], { type: 'image/jpg' })
    },
  },
}
</script>
